import React, { useEffect, useMemo, useState } from "react";
import {
    Alert, Box, Chip, Dialog, DialogContent, DialogTitle, FormControlLabel, Grid, IconButton,
    Paper, Switch, Table, TableBody, TableCell, TableHead, TableRow, ToggleButton, ToggleButtonGroup,
    Tooltip, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DataGrid } from "@mui/x-data-grid";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV2";
import moment from "moment";
import axios from "axios";
import { APIURL } from "../../config";

/*
 * Team Workload: for a period, how each employee's tasks stand (completed,
 * open, overdue, never checked in) and the hours they logged.
 */

const DATE_FORMAT = "YYYY-MM-DD";

const PRESETS = [
    { key: "thisWeek", label: "This week", range: () => [moment().startOf("week"), moment().endOf("week")] },
    { key: "lastWeek", label: "Last week", range: () => [moment().subtract(1, "week").startOf("week"), moment().subtract(1, "week").endOf("week")] },
    { key: "last30", label: "Last 30 days", range: () => [moment().subtract(29, "days"), moment()] },
    { key: "thisMonth", label: "This month", range: () => [moment().startOf("month"), moment().endOf("month")] },
    { key: "lastMonth", label: "Last month", range: () => [moment().subtract(1, "month").startOf("month"), moment().subtract(1, "month").endOf("month")] },
];

const COLORS = { completed: "#4caf50", open: "#3FA7D6", overdue: "#d32f2f", notCheckedIn: "#ed6c02" };

const STATUS_CHIPS = {
    completed: { label: "Completed", color: "success" },
    open: { label: "Open", color: "info" },
    overdue: { label: "Overdue", color: "error" },
};

const hours = (minutes) => (minutes ? (minutes / 60).toFixed(1) : "0");

const errorMessage = (error, fallback) => error.response?.data?.message || fallback;

function KpiCard({ label, value, color }) {
    return (
        <Paper sx={{ px: 2, py: 2, height: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
                {label}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2, color: value ? color : undefined }}>
                {value === null ? "—" : value}
            </Typography>
        </Paper>
    );
}

/* Completed / open / overdue as one bar, so the mix reads at a glance down the column. */
function StatusBar({ row }) {
    if (!row.scheduled) return <Typography variant="caption" color="text.secondary">No tasks</Typography>;
    const parts = [
        ["completed", row.completed],
        ["open", row.open_on_time],
        ["overdue", row.overdue],
    ].filter(([, count]) => count > 0);
    return (
        <Tooltip title={`${row.completed} completed · ${row.open_on_time} open · ${row.overdue} overdue`}>
            <Box sx={{ display: "flex", width: "100%", height: 10, borderRadius: 5, overflow: "hidden", bgcolor: "action.hover" }}>
                {parts.map(([key, count]) => (
                    <Box key={key} sx={{ width: `${(count / row.scheduled) * 100}%`, bgcolor: COLORS[key] }} />
                ))}
            </Box>
        </Tooltip>
    );
}

const countCell = (color) => (params) => (
    <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
        <Typography variant="body2" sx={{ fontWeight: params.value ? 600 : 400, color: params.value ? color : "text.secondary" }}>
            {params.value}
        </Typography>
    </Box>
);

const columns = [
    { field: "employee_name", headerName: "Employee", flex: 1, minWidth: 180 },
    { field: "status_mix", headerName: "Status", flex: 1, minWidth: 160, sortable: false, renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%", width: "100%" }}><StatusBar row={params.row} /></Box>
    ) },
    { field: "scheduled", headerName: "Scheduled", type: "number", width: 110 },
    { field: "completed", headerName: "Completed", type: "number", width: 110 },
    { field: "open_on_time", headerName: "Open", type: "number", width: 90 },
    { field: "overdue", headerName: "Overdue", type: "number", width: 100, renderCell: countCell(COLORS.overdue) },
    { field: "not_checked_in", headerName: "Not Checked In", type: "number", width: 140, renderCell: countCell(COLORS.notCheckedIn) },
    {
        field: "completion",
        headerName: "Completion",
        type: "number",
        width: 120,
        valueGetter: (value, row) => (row.scheduled ? Math.round((row.completed / row.scheduled) * 100) : null),
        valueFormatter: (value) => (value === null || value === undefined ? "—" : `${value}%`),
    },
    {
        field: "minutes_worked",
        headerName: "Hours",
        type: "number",
        width: 100,
        renderCell: (params) => (
            params.row.unusable_sessions ? (
                <Tooltip title={`${params.row.unusable_sessions} check-in session(s) not counted: over 24 hours, or 0 minutes or less. Click the row to see them.`}>
                    <span>{hours(params.value)} *</span>
                </Tooltip>
            ) : hours(params.value)
        ),
    },
];

const taskColumns = [
    { field: "start_date", headerName: "Start", width: 105, valueFormatter: (value) => moment(value, DATE_FORMAT).format("MM/DD/YY") },
    { field: "due_date", headerName: "Due", width: 105, valueFormatter: (value) => moment(value, DATE_FORMAT).format("MM/DD/YY") },
    { field: "tas_type", headerName: "Type", width: 140 },
    { field: "tas_title", headerName: "Task", flex: 1, minWidth: 180 },
    { field: "cus_name", headerName: "Customer", flex: 0.8, minWidth: 140, valueFormatter: (value) => value || "—" },
    {
        field: "status",
        headerName: "Status",
        width: 115,
        renderCell: (params) => {
            const chip = STATUS_CHIPS[params.value];
            return chip ? <Chip size="small" label={chip.label} color={chip.color} variant="outlined" /> : params.value;
        },
    },
    { field: "checked_in", headerName: "Checked In", width: 110, valueFormatter: (value) => (value ? "Yes" : "No") },
    { field: "minutes_worked", headerName: "Hours", type: "number", width: 90, valueFormatter: (value) => (value ? hours(value) : "—") },
];

const SESSION_REASONS = {
    over_max: "Over 24 hours, likely a missed check-out",
    not_after_check_in: "0 minutes or less",
};

const SESSION_TIME_FORMAT = "YYYY-MM-DD HH:mm";

const sessionLength = (minutes) => (minutes > 60 ? `${hours(minutes)} h` : `${minutes} min`);

/* The check-in sessions behind the asterisk on an employee's hours. Keyed on
   check-in date, like the hours, so a task here may have started before the period. */
function UncountedSessions({ sessions, error }) {
    if (!error && sessions.length === 0) return null;
    return (
        <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Check-in sessions not counted in hours{sessions.length ? ` (${sessions.length})` : ""}
            </Typography>
            <Typography variant="caption" color="text.secondary">
                These sessions began in the period but are left out of Hours because their length can&apos;t be right.
                Correct the check-in or check-out time to have them counted.
            </Typography>
            {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
            {sessions.length > 0 &&
                <Table size="small" sx={{ mt: 1 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Checked In</TableCell>
                            <TableCell>Checked Out</TableCell>
                            <TableCell align="right">Length</TableCell>
                            <TableCell>Why not counted</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Task</TableCell>
                            <TableCell>Customer</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sessions.map((s) => (
                            <TableRow key={s.session_id}>
                                <TableCell>{moment(s.check_in, SESSION_TIME_FORMAT).format("MM/DD/YY h:mm A")}</TableCell>
                                <TableCell>{moment(s.check_out, SESSION_TIME_FORMAT).format("MM/DD/YY h:mm A")}</TableCell>
                                <TableCell align="right">{sessionLength(s.minutes)}</TableCell>
                                <TableCell>{SESSION_REASONS[s.reason] || s.reason}</TableCell>
                                <TableCell>{s.tas_type}</TableCell>
                                <TableCell>{s.tas_title}</TableCell>
                                <TableCell>{s.cus_name || "—"}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>}
        </Box>
    );
}

/* The tasks behind one employee's row. */
function TeamMemberTasks({ employee, filters, user, onClose }) {
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [sessionsError, setSessionsError] = useState(null);

    useEffect(() => {
        if (!employee) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        axios.get(APIURL + "/team-workload/tasks", { params: { ...filters, comp_id: user.comp_id, emp_id: user.emp_id, team_member_id: employee.emp_id } })
            .then((res) => { if (!cancelled) setTasks(res.data); })
            .catch((err) => { if (!cancelled) { setTasks([]); setError(errorMessage(err, "Could not load tasks.")); } })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [employee, filters, user.comp_id, user.emp_id]);

    // Only asked for when the row shows an asterisk.
    useEffect(() => {
        setSessions([]);
        setSessionsError(null);
        if (!employee || !employee.unusable_sessions) return;
        let cancelled = false;
        axios.get(APIURL + "/team-workload/uncounted-sessions", { params: { ...filters, comp_id: user.comp_id, emp_id: user.emp_id, team_member_id: employee.emp_id } })
            .then((res) => { if (!cancelled) setSessions(res.data); })
            .catch((err) => { if (!cancelled) setSessionsError(errorMessage(err, "Could not load the check-in sessions that weren't counted.")); });
        return () => { cancelled = true; };
    }, [employee, filters, user.comp_id, user.emp_id]);

    return (
        <Dialog open={!!employee} onClose={onClose} fullWidth maxWidth="lg">
            <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1 }}>
                <Box>
                    {employee?.employee_name}
                    <Typography variant="body2" color="text.secondary">
                        Tasks starting {moment(filters.from, DATE_FORMAT).format("MM/DD/YYYY")} – {moment(filters.to, DATE_FORMAT).format("MM/DD/YYYY")}
                    </Typography>
                </Box>
                <IconButton onClick={onClose}><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent>
                {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
                <Box sx={{ height: 480 }}>
                    <DataGrid
                        rows={tasks}
                        columns={taskColumns}
                        getRowId={(row) => row.tas_id}
                        loading={loading}
                        density="compact"
                        disableRowSelectionOnClick
                    />
                </Box>
                <UncountedSessions sessions={sessions} error={sessionsError} />
            </DialogContent>
        </Dialog>
    );
}

export default function TeamWorkload(props) {
    const { user } = props;
    const [preset, setPreset] = useState("last30");
    const [range, setRange] = useState(() => PRESETS.find((p) => p.key === "last30").range());
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [showIdle, setShowIdle] = useState(false);
    const [selected, setSelected] = useState(null);

    const filters = useMemo(() => ({
        from: range[0].format(DATE_FORMAT),
        to: range[1].format(DATE_FORMAT),
    }), [range]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setLoadError(null);
        // program_id is added by App's axios interceptor from the program picker.
        axios.get(APIURL + "/team-workload", { params: { ...filters, comp_id: user.comp_id, emp_id: user.emp_id } })
            .then((res) => { if (!cancelled) setEmployees(res.data.employees || []); })
            .catch((err) => {
                if (cancelled) return;
                setEmployees([]);
                // Shown instead of an empty grid, which would read as "nobody has work".
                setLoadError(errorMessage(err, "Could not load team workload. Try again in a moment."));
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [filters, user.comp_id, user.emp_id, props.selectedProgramId]);

    const choosePreset = (key) => {
        if (!key) return;
        setPreset(key);
        setRange(PRESETS.find((p) => p.key === key).range());
    };

    const chooseDate = (index, date) => {
        if (!date || Number.isNaN(date.getTime())) return;
        const next = [...range];
        next[index] = moment(date);
        if (next[1].isBefore(next[0], "day")) return;
        setPreset(null);
        setRange(next);
    };

    const withWork = employees.filter((e) => e.scheduled || e.minutes_worked);
    const rows = showIdle ? employees : withWork;
    const total = (field) => (loadError ? null : employees.reduce((sum, e) => sum + (e[field] || 0), 0));

    return (
        <Box sx={{ pt: 1, pb: 1, height: "calc(100vh - 85px)", display: "flex", flexDirection: "column", gap: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
                <ToggleButtonGroup size="small" exclusive value={preset} onChange={(e, key) => choosePreset(key)}>
                    {PRESETS.map((p) => <ToggleButton key={p.key} value={p.key} sx={{ textTransform: "none", px: 1.5 }}>{p.label}</ToggleButton>)}
                </ToggleButtonGroup>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                        label="From"
                        value={range[0].toDate()}
                        maxDate={range[1].toDate()}
                        onChange={(date) => chooseDate(0, date)}
                        slotProps={{ textField: { size: "small", sx: { width: 170 } } }}
                    />
                    <DatePicker
                        label="To"
                        value={range[1].toDate()}
                        minDate={range[0].toDate()}
                        onChange={(date) => chooseDate(1, date)}
                        slotProps={{ textField: { size: "small", sx: { width: 170 } } }}
                    />
                </LocalizationProvider>
            </Box>

            <Grid container spacing={1}>
                <Grid size={{ xs: 6, md: 2 }}><KpiCard label="Employees With Work" value={loadError ? null : withWork.length} /></Grid>
                <Grid size={{ xs: 6, md: 2 }}><KpiCard label="Scheduled" value={total("scheduled")} /></Grid>
                <Grid size={{ xs: 6, md: 2 }}><KpiCard label="Completed" value={total("completed")} /></Grid>
                <Grid size={{ xs: 6, md: 2 }}><KpiCard label="Overdue" value={total("overdue")} color={COLORS.overdue} /></Grid>
                <Grid size={{ xs: 6, md: 2 }}><KpiCard label="Not Checked In" value={total("not_checked_in")} color={COLORS.notCheckedIn} /></Grid>
                <Grid size={{ xs: 6, md: 2 }}><KpiCard label="Hours Booked" value={loadError ? null : hours(total("minutes_worked"))} /></Grid>
            </Grid>

            <Paper sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", p: 2, pt: 0.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", columnGap: 2, mb: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "baseline", flexWrap: "wrap", columnGap: 1.5 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Team Workload
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Tasks starting in the period (checklist items aren't counted). Overdue: still open after its end date.
                            Not checked in: start date has passed with no check-in. Click a row for the tasks.
                        </Typography>
                    </Box>
                    <FormControlLabel
                        control={<Switch size="small" checked={showIdle} onChange={(e) => setShowIdle(e.target.checked)} />}
                        label={<Typography variant="body2">Show employees with no work</Typography>}
                    />
                </Box>
                {loadError && <Alert severity="error" sx={{ mb: 1 }}>{loadError}</Alert>}
                <Box sx={{ flex: 1, minHeight: 0 }}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        getRowId={(row) => row.emp_id}
                        loading={loading}
                        density="compact"
                        disableRowSelectionOnClick
                        onRowClick={(params) => setSelected(params.row)}
                        initialState={{ sorting: { sortModel: [{ field: "overdue", sort: "desc" }] } }}
                        sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
                        showToolbar
                    />
                </Box>
            </Paper>

            <TeamMemberTasks employee={selected} filters={filters} user={user} onClose={() => setSelected(null)} />
        </Box>
    );
}
